/**
 * airan-ntn — Topology Management
 * Satellite constellation, UE placement, ISL mesh, simple UE mobility
 * Note: Lévy walk removed - satellites already have orbital mechanics; UEs use simple random walk
 */

import {
  Vec3, Vec2, UserEquipment, Satellite, GroundStation, SimConfig,
  SimState, ModelWeights, TrainSample, ISLLink
} from './types.js';
import {
  EARTH_RADIUS, UE_PALETTE, SNR_THRESHOLDS,
  TRAIL_MAX, SIM_AREA_SIZE
} from './config.js';
import {
  vec3Len, vec3Sub, vec3Norm, vec3Dot, vec3Scale, vec3Add
} from './types.js';
import {
  llaToEcef, ecefToLla
} from './orbital.js';
import {
  createSatellites, createGroundStations, calculateISLLinks,
  updateFeederLinks, getBestSatellite, propagateSatellite
} from './orbital.js';
import { random, fadingGain, noisePowerdBm } from './channel.js';

// ──────────────────────────────────────────────────────────────────────────────
// UE Placement & Data Partitioning
// ──────────────────────────────────────────────────────────────────────────────

function dirichlet(alpha: number[], size: number): number[][] {
  const K = alpha.length;
  const samples: number[][] = [];
  for (let i = 0; i < size; i++) {
    const g = alpha.map(a => gammaSample(a));
    const s = g.reduce((a, b) => a + b, 0);
    samples.push(g.map(v => v / s));
  }
  return samples;
}

function gammaSample(shape: number): number {
  if (shape < 1) return gammaSample(1 + shape) * Math.pow(random(), 1 / shape);
  const d = shape - 1 / 3;
  const c = 1 / Math.sqrt(9 * d);
  for (;;) {
    let x: number, v: number;
    do {
      x = (random() - 0.5) * 2; // Simple normal approximation
      v = 1 + c * x;
    } while (v <= 0);
    v = v * v * v;
    const u = random();
    if (u < 1 - 0.0331 * x ** 4) return d * v;
    if (Math.log(u) < 0.5 * x * x + d * (1 - v + Math.log(v))) return d * v;
  }
}

function generateClassData(numClasses: number, numFeatures: number, totalSamples: number): TrainSample[] {
  const centers: number[][] = [];
  for (let c = 0; c < numClasses; c++) {
    const angle = (2 * Math.PI * c) / numClasses;
    const radius = 2.5;
    const center = [radius * Math.cos(angle), radius * Math.sin(angle)];
    for (let f = 2; f < numFeatures; f++) center.push((random() - 0.5) * 0.5);
    centers.push(center);
  }

  const samples: TrainSample[] = [];
  for (let i = 0; i < totalSamples; i++) {
    const c = i % numClasses;
    const x = new Float32Array(centers[c].map((v, idx) => v + (idx < 2 ? (random() - 0.5) * 0.6 : (random() - 0.5) * 0.6)));
    samples.push({ x, y: c });
  }
  return shuffle(samples);
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export function partitionData(config: SimConfig): Map<number, TrainSample[]> {
  const { numUEs, numClasses, numFeatures, samplesPerUE, nonIIDAlpha } = config;
  const total = numUEs * samplesPerUE;
  const global = generateClassData(numClasses, numFeatures, total);

  // Group by class
  const byClass: TrainSample[][] = Array.from({ length: numClasses }, () => []);
  for (const s of global) byClass[s.y].push(s);

  // Dirichlet proportions: shape [numUEs × numClasses]
  const alpha = new Array(numClasses).fill(nonIIDAlpha);
  const proportions = dirichlet(alpha, numUEs);

  const partitions = new Map<number, TrainSample[]>();
  for (let ue = 0; ue < numUEs; ue++) partitions.set(ue, []);

  for (let c = 0; c < numClasses; c++) {
    const pool = byClass[c];
    let idx = 0;
    for (let ue = 0; ue < numUEs; ue++) {
      const count = Math.max(1, Math.round(proportions[ue][c] * pool.length));
      const end = Math.min(idx + count, pool.length);
      (partitions.get(ue) as TrainSample[]).push(...pool.slice(idx, end));
      idx = end;
      if (idx >= pool.length) break;
    }
  }
  return partitions;
}

// ──────────────────────────────────────────────────────────────────────────────
// Model Weight Helpers
// ──────────────────────────────────────────────────────────────────────────────

export function zeroWeights(numFeatures: number, numClasses: number): ModelWeights {
  return {
    w: new Float32Array(numClasses * numFeatures).map(() => (random() - 0.5) * 0.01),
    b: new Float32Array(numClasses).fill(0),
    numFeatures,
    numClasses,
    version: 0
  };
}

export function cloneWeights(m: ModelWeights): ModelWeights {
  return {
    w: new Float32Array(m.w),
    b: new Float32Array(m.b),
    numFeatures: m.numFeatures,
    numClasses: m.numClasses,
    version: m.version
  };
}

export function addWeights(a: ModelWeights, b: ModelWeights): ModelWeights {
  return {
    w: new Float32Array(a.w.map((v, i) => v + b.w[i])),
    b: new Float32Array(a.b.map((v, i) => v + b.b[i])),
    numFeatures: a.numFeatures,
    numClasses: a.numClasses,
    version: a.version
  };
}

export function scaleWeights(m: ModelWeights, s: number): ModelWeights {
  return {
    w: new Float32Array(m.w.map(v => v * s)),
    b: new Float32Array(m.b.map(v => v * s)),
    numFeatures: m.numFeatures,
    numClasses: m.numClasses,
    version: m.version
  };
}

export function weightDiffNorm2(a: ModelWeights, b: ModelWeights): number {
  let s = 0;
  for (let i = 0; i < a.w.length; i++) s += (a.w[i] - b.w[i]) ** 2;
  for (let i = 0; i < a.b.length; i++) s += (a.b[i] - b.b[i]) ** 2;
  return s;
}

export function paramCount(m: ModelWeights): number {
  return m.w.length + m.b.length;
}

// ──────────────────────────────────────────────────────────────────────────────
// Simple UE Mobility (Random Walk on Earth Surface)
// ──────────────────────────────────────────────────────────────────────────────

export function ueWalkStep(
  ues: UserEquipment[],
  satellites: Satellite[],
  config: SimConfig
): void {
  const dt = config.roundDuration;
  const walkSpeed = config.ueSpeed; // m/s
  const distPerRound = walkSpeed * dt; // meters per round

  for (const ue of ues) {
    if (!config.walkEnabled) continue;

    // Simple random walk: small random displacement
    const angle = random() * 2 * Math.PI;
    const step = distPerRound * (0.5 + random() * 0.5); // 0.5-1.0 x distPerRound

    const dx = Math.cos(angle) * step;
    const dy = Math.sin(angle) * step;

    // Update position (convert to lat/lon, step, convert back)
    const { lat, lon } = ecefToLla(ue.position);
    const R = EARTH_RADIUS;
    const deltaLat = (dy / R) * (180 / Math.PI);
    const deltaLon = (dx / (R * Math.cos(lat * Math.PI / 180))) * (180 / Math.PI);

    const newLat = Math.max(-80, Math.min(80, lat + deltaLat));
    const newLon = ((lon + deltaLon + 180) % 360) - 180;

    ue.position = llaToEcef(newLat, newLon, 0);

    // Update velocity for rendering
    ue.velocity = { x: dx / dt, y: dy / dt, z: 0 };

    // Maintain trail
    ue.trail.push({ ...ue.position });
    if (ue.trail.length > TRAIL_MAX) ue.trail.shift();

    // Update serving satellite
    const bestSat = getBestSatellite(ue.position, satellites, config.minElevationDeg);
    if (bestSat && bestSat.id !== ue.servingSatId) {
      ue.servingSatId = bestSat.id;
      ue.bsId = bestSat.id;

      const elevation = calculateElevation(ue.position, bestSat.position);
      const pl = pathLoss(ue.position, bestSat.position, config, elevation);
      const gain = fadingGain(config.channelModel, config.ricianK);
      const noise = noisePowerdBm(config.bandwidthMHz);
      ue.pathLoss = pl;
      ue.channelGain = gain;
      ue.snr = computeSNR(ue.txPower, pl, gain, noise);
      ue.sinr = ue.snr;
      ue.angle = azimuth(ue.position, bestSat.position);
    }
  }
}

function pathLoss(uePos: Vec3, satPos: Vec3, config: SimConfig, elevation: number): number {
  const dx = uePos.x - satPos.x;
  const dy = uePos.y - satPos.y;
  const dz = uePos.z - satPos.z;
  const distKm = Math.sqrt(dx*dx + dy*dy + dz*dz) / 1000;
  const d = Math.max(distKm, 0.001);

  let slope: number;
  switch (config.channelModel) {
    case 'NTN-LOS': slope = 20; break;
    case 'NTN-NLOS': slope = 36.7; break;
    default: slope = config.pathLossExponent > 0 ? 10 * config.pathLossExponent : 20;
  }

  const fspl = 32.4 + slope * Math.log10(d) + 20 * Math.log10(config.carrierFreqGHz);
  return fspl + (random() - 0.5) * 2 * config.shadowingStd;
}

function calculateElevation(uePos: Vec3, satPos: Vec3): number {
  const ueToSat = vec3Norm(vec3Sub(satPos, uePos));
  const ueUp = vec3Norm(uePos);
  const elevationRad = Math.asin(Math.max(-1, Math.min(1, vec3Dot(ueToSat, ueUp))));
  return elevationRad * 180 / Math.PI;
}

function azimuth(uePos: Vec3, satPos: Vec3): number {
  const dx = satPos.x - uePos.x;
  const dy = satPos.y - uePos.y;
  return Math.atan2(dy, dx);
}

function computeSNR(txPower: number, pathLossDb: number, gain: number, noisePower: number): number {
  const gainDb = 10 * Math.log10(Math.max(gain, 1e-12));
  return txPower - pathLossDb + gainDb - noisePower;
}

// ──────────────────────────────────────────────────────────────────────────────
// UE Creation
// ──────────────────────────────────────────────────────────────────────────────

export function createUEs(config: SimConfig, satellites: Satellite[], data: Map<number, TrainSample[]>): UserEquipment[] {
  const ues: UserEquipment[] = [];

  for (let id = 0; id < config.numUEs; id++) {
    // Random position within simulation area
    const lat = -60 + random() * 120;
    const lon = -180 + random() * 360;
    const pos = llaToEcef(lat, lon, 0);

    // Find best serving satellite
    const bestSat = getBestSatellite(pos, satellites, config.minElevationDeg);
    const servingSatId = bestSat?.id ?? null;

    // Initial channel
    let snr = 0, gain = 1, pl = 0;
    if (bestSat) {
      const elevation = calculateElevation(pos, bestSat.position);
      pl = pathLoss(pos, bestSat.position, config, elevation);
      gain = fadingGain(config.channelModel, config.ricianK);
      const noise = noisePowerdBm(config.bandwidthMHz);
      snr = computeSNR(23, pl, gain, noise);
    }

    ues.push({
      id,
      position: pos,
      velocity: { x: 0, y: 0, z: 0 },
      servingSatId,
      candidateSats: [],
      snr,
      sinr: snr,
      pathLoss: pl,
      dopplerShift: 0,
      channelGain: gain,
      walkTarget: { x: 0, y: 0, z: 0 },
      walkRemaining: 0,
      pauseLeft: 0,
      trail: [],
      dataSize: config.samplesPerUE,
      localWeights: zeroWeights(config.numFeatures, config.numClasses),
      selected: false,
      lastRound: -1,
      bsId: servingSatId ?? -1,
      angle: 0,
      batterySoc: 1.0,
      txPower: 23,
      color: UE_PALETTE[id % UE_PALETTE.length]
    });
  }

  return ues;
}

// ──────────────────────────────────────────────────────────────────────────────
// Topology Initialization & Update
// ──────────────────────────────────────────────────────────────────────────────

export function initTopology(config: SimConfig): SimState {
  const startTime = 0;

  // Create constellation
  const satellites = createSatellites(config, startTime);

  // Create ground stations
  const groundStations = createGroundStations(config);

  // Calculate initial ISL links
  const islLinks = calculateISLLinks(satellites, config.maxISLRange * 1000);
  for (const sat of satellites) {
    sat.islLinks = islLinks.get(sat.id) ?? [];
    sat.islNeighbors = sat.islLinks.map(l => l.neighborId);
  }

  // Update feeder links
  updateFeederLinks(satellites, groundStations, config.minElevationDeg);

  // Initialize data partitions
  const data = partitionData(config);

  // Create UEs
  const ues = createUEs(config, satellites, data);

  // Update initial candidate satellites for each UE
  for (const ue of ues) {
    ue.candidateSats = satellites
      .filter(s => calculateElevation(ue.position, s.position) >= config.minElevationDeg)
      .map(s => s.id)
      .slice(0, 5);
  }

  // Global model
  const globalWeights = zeroWeights(config.numFeatures, config.numClasses);

  return {
    config,
    satellites,
    groundStations,
    ues,
    globalWeights,
    round: 0,
    history: [],
    spaceRics: new Map(),
    xApps: new Map(),
    kpmDatabase: new Map(),
    pendingActions: [],
    feederLinkHistory: new Map()
  };
}

export function updateTopology(state: SimState, dt: number): SimState {
  const { config, satellites, groundStations, ues } = state;

  // Propagate satellites (orbital mechanics)
  const newSatellites = satellites.map(sat => propagateSatellite(sat, dt));

  // Recalculate ISL links
  const islLinks = calculateISLLinks(newSatellites, config.maxISLRange * 1000);
  for (const sat of newSatellites) {
    sat.islLinks = islLinks.get(sat.id) ?? [];
    sat.islNeighbors = sat.islLinks.map(l => l.neighborId);
  }

  // Update feeder links
  updateFeederLinks(newSatellites, groundStations, config.minElevationDeg);

  // Simple UE random walk
  ueWalkStep(ues, newSatellites, config);

  // Update candidate satellites for each UE
  for (const ue of ues) {
    ue.candidateSats = newSatellites
      .filter(s => calculateElevation(ue.position, s.position) >= config.minElevationDeg)
      .map(s => s.id)
      .slice(0, 5);
  }

  return {
    ...state,
    satellites: newSatellites
  };
}

// Re-export from orbital
export { propagateSatellite } from './orbital.js';